import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import { StandardiseCoinName } from "../utils/PriceUtils";

export interface ExchangeMarket {
    exchange: string,
    market: string,
}

// Define a type for the slice state
interface PriceState {
  requestNewConnection: boolean;
  isConnected: boolean;
  lastUpdated: number;
  //prices: { [symbol: string]: Price[] };
  [key: string]: AvailableQuotes[] | string | string[] | boolean | number | Price[] | CurrentPrice[] | ExchangeMarket[]; // | { [symbol: string]: Price[] }; // Delete the last one when we've refactored
  availableQuotes: AvailableQuotes[];
  exchanges: string[];
  exchangeMarkets: ExchangeMarket[];
  defaultSymbols: string;
  watchedSymbols: string[];
  currentPrices: CurrentPrice[];
  //history: { [symbol: string]: HistoricalPrice[] };
  //movers: Mover[];
  //moverCache: Mover[];
}

export interface CurrentPrice {
    base: string,
    quote: string,
    price: number,
}

export interface Price {
  exchange: string;
  market: string;
  base: string;
  quote: string;
  price: number;
  history: HistoricalPrice[],
  currentKline: HistoricalPrice | null,
  bid: number;
  ask: number;
}

export interface AvailableQuotes {
    exchange: string,
    market: string,
    base: string,
    exchangeBase: string,
    quotes: string[],
}

const movement5sec = 0.025;
const movement15sec = 0.05;
const movement30sec = 0.07;

interface MoverCache extends Mover {}

interface Mover {
  base: string;
  price: number;
  price5sAgo: number;
  price15sAgo: number;
  price30sAgo: number;
  time: number | undefined; // Used for the cache object
}

interface HistoricalPrice {
  timestamp: number;
  price: number;
  open?: number;
    high?: number;
    low?: number;
    close?: number;
}

// Define the initial state using that type
const initialState: PriceState = {
  requestNewConnection: false,
  isConnected: false,
  lastUpdated: new Date().valueOf(),
  //prices: {},
  availableQuotes: [],
  exchanges: ["Binance", "GateIo", "KuCoin", "MEXC", "ByBit"],
  exchangeMarkets: [ 

    // Make sure this order matches the order in the price oracle
    // It only sends the first exchange / market match to reduce bandwidth

    { exchange: "Binance", market: "Spot" }, // priority 1
    { exchange: "ByBit", market: "Futures" },  // priority 2
    { exchange: "GateIo", market: "Spot" },  // priority 3
    { exchange: "MEXC", market: "Spot" },  // priority 4
    { exchange: "Binance", market: "Futures" }, 
    //{ exchange: "BingX", market: "Spot" }, 
    { exchange: "Bitget", market: "Spot" }, 
    { exchange: "Bitget", market: "Futures" },
    { exchange: "ByBit", market: "Spot" }, 
    { exchange: "Coinbase", market: "Spot" }, 
    { exchange: "GateIo", market: "Futures" },
    { exchange: "KuCoin", market: "Spot" }, 
    { exchange: "OKX", market: "Spot" }, 
    //{ exchange: "WooX", market: "Spot" }, 
    //{ exchange: "WooX", market: "Futures" }
],
  defaultSymbols: "BTC,ETH",
  watchedSymbols: ["BTC", "ETH"],
  currentPrices: [],
  //history: {},
  //movers: [],
  //moverCache: [],
};

export const priceSlice = createSlice({
  name: "prices",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    requestConnect: (state) => {
      state.requestNewConnection = true;
    },
    connectionEstablished: (state) => {
      state.isConnected = true;
      state.requestNewConnection = false;
    },
    addPrice: (state, action: PayloadAction<string>) => { 

        console.log(`addPrice: ${action.payload}`);

        // Only update if it's not already tracked
        if (!state[StandardiseCoinName(action.payload, "", "")]) {
            state[StandardiseCoinName(action.payload, "", "")] = [] as Price[];
        }
        
        if (state.watchedSymbols.indexOf(action.payload) === -1) {
            state.watchedSymbols = [...state.watchedSymbols.filter(s => StandardiseCoinName(s, "", "") !== StandardiseCoinName(action.payload, "", "")), StandardiseCoinName(action.payload, "", "")].filter((s, i, a) => a.indexOf(s) === i);
        }
    },
    addCurrentPrice: (state, action: PayloadAction<string>) => { 
        //console.log(`addCurrentPrice: ${action.payload}`);
        state.currentPrices = [...state.currentPrices.filter(s => StandardiseCoinName(s.base, "", "") !== StandardiseCoinName(action.payload, "", "")), { "base": StandardiseCoinName(action.payload, "", ""), "quote": "USDT", price: 0}].filter((s, i, a) => a.indexOf(s) === i);
    },
    removePrice: (state, action: PayloadAction<string>) => {
        //console.log(`removePrice: ${action.payload}`);
        state.watchedSymbols = state.watchedSymbols.filter(s => s !== action.payload).filter((s, i, a) => a.indexOf(s) === i);
        delete state[action.payload];
     },
     removeCurrentPrice: (state, action: PayloadAction<string>) => {
        //console.log(`removeCurrentPrice: ${action.payload}`);
        state.currentPrices = state.currentPrices.filter(s => StandardiseCoinName(s.base, "", "") !== StandardiseCoinName(action.payload, "", "")).filter((s, i, a) => a.indexOf(s) === i);
     },
    updateQuotes: (state, action: PayloadAction<AvailableQuotes[]>) => {
        state.availableQuotes = action.payload;
      },
    updatePrices: (state, action: PayloadAction<Price[]>) => {
      let currentTime = new Date().valueOf();
      state.lastUpdated = currentTime;

      // Prices
      for (let i = 0; i < action.payload.length; i++) {
        const incoming = action.payload[i];
        const baseName = StandardiseCoinName(incoming.base, "", "");

        if (state[baseName]) {
            let exchangeQuotePrice = (state[baseName] as Price[])?.find(p => p.exchange === incoming.exchange && p.quote === incoming.quote);
            if (exchangeQuotePrice) {

                if (incoming.history) {

                    // Log this action
                    // console.log(`${new Date().valueOf()} updatePricesWithHistory: ${incoming.base} ${incoming.exchange} ${incoming.quote}`);

                    exchangeQuotePrice = {...incoming};
                }
                else {

                    // Log this action
                    // console.log(`${new Date().valueOf()} updatePricesWithoutHistory: ${incoming.base} ${incoming.exchange} ${incoming.quote}`);

                    exchangeQuotePrice.price = incoming.price;

                    if (!exchangeQuotePrice.history) {
                        exchangeQuotePrice.history = [];
                    }

                    if (incoming.currentKline !== null) {
                        let index = exchangeQuotePrice.history.findIndex(q => q.timestamp === incoming.currentKline?.timestamp);
                        if (index === -1) {
                            // Add to the price history
                            exchangeQuotePrice.history.push(incoming.currentKline as HistoricalPrice);
                        }
                        else {
                            // Replace the price history for this item
                            const newKline = incoming.currentKline as HistoricalPrice;
                            const existing = exchangeQuotePrice.history[index];
                            if (newKline.timestamp !== existing.timestamp
                                || newKline.price !== existing.price
                                || newKline.open !== existing.open
                                || newKline.high !== existing.high
                                || newKline.low !== existing.low
                                || newKline.close !== existing.close) {
                                exchangeQuotePrice.history.splice(index, 1, newKline);
                                //exchangeQuotePrice.history.length = Math.min(exchangeQuotePrice.history.length, 6 * 60 * 1); // Keep 1 hour max (may need to increase this somehow)
                            }
                        }

                        // Set the max length of the array (3 hours)
                        if (exchangeQuotePrice.history.length > 6 * 60 * 3) {
                            exchangeQuotePrice.history.splice(0, 1);//.length = 6 * 60 * 3;
                        }
                    }

                    // Log the current price
                    // console.log(`${new Date().valueOf()} updatePricesWithoutHistory: ${incoming.base} ${incoming.exchange} ${incoming.quote} ${incoming.price}`);
                }
            }
            else {
                // On a new subscription, wait for the record with history (in case they're out of sync)
                if (incoming.history) {

                    // Log this action
                    // console.log(`${new Date().valueOf()} updatePrices: ${incoming.base} ${incoming.exchange} ${incoming.quote}`);

                    // Uadd the payload to the state
                    /*
                    if (!state[incoming.base]) {
                        state[incoming.base] = [];
                    }
                    else {
                        // Remove the record without history
                        state[incoming.base] = (state[incoming.base] as Price[]).filter(p => p.exchange !== incoming.exchange || p.quote !== incoming.quote);
                    }
*/
                    state[baseName] 
                        = [...(state[baseName] as Price[]).filter(p => p.exchange !== incoming.exchange || p.quote !== incoming.quote), incoming];

                    //state[incoming.base] = incoming as Price[];
                }
            }

            //state.prices[incoming.base].price  = [...state.prices[incoming.base]?.filter(p => p.exchange !== incoming.exchange || p.quote !== incoming.quote), incoming];
        }

        if (incoming.quote === "USDT") {
            let currentPrice = state.currentPrices.find(p => StandardiseCoinName(p.base, "", "") === baseName);
            if (currentPrice) {
                currentPrice.price = incoming.price;
                state.currentPrices = [...state.currentPrices.filter(p => StandardiseCoinName(p.base, "", "") !== baseName), currentPrice];
            }
        }
      }
    },

    updateDefaultSymbols: (state, action: PayloadAction<string>) => {
      state.defaultSymbols = action.payload;
    },
    
  },
});

export const { requestConnect, connectionEstablished, updatePrices, updateQuotes, addPrice, removePrice, addCurrentPrice, removeCurrentPrice, updateDefaultSymbols } =
  priceSlice.actions;

// Other code such as selectors can use the imported `RootState` type
//export const prices = (state: RootState) => state.prices.prices;
export const selectWatchedSymbols = (state: RootState) => state.prices.watchedSymbols;
export const selectAvailableQuotes = (state: RootState) => state.prices.availableQuotes;
export const selectExchanges = (state: RootState) => state.prices.exchanges;
export const selectExchangeMarkets = (state: RootState) => state.prices.exchangeMarkets;
export const selectDefaultSymbols = (state: RootState) => state.prices.defaultSymbols;
export const selectLastUpdated = (state: RootState) => state.prices.lastUpdated;
export const selectCurrentPrices = (state: RootState) => state.prices.currentPrices;
//export const history = (state: RootState) => state.prices.history;
//export const movers = (state: RootState) => state.prices.movers;

// Get all prices for a specific symbol
export const selectPrices = (state: RootState, key: string) => {
    return state.prices[key] as Price[];
};

export default priceSlice.reducer;
